import { createContext, useContext, useState, type ReactNode } from 'react'
import { startOfWeek } from 'date-fns'
import { useSettings } from './settings-context'

interface PlanningWeekContextType {
  currentWeekStart: Date
  setCurrentWeekStart: (date: Date) => void
  goToPreviousWeek: () => void
  goToNextWeek: () => void
}

const PlanningWeekContext = createContext<PlanningWeekContextType | undefined>(undefined)

interface PlanningWeekProviderProps {
  children: ReactNode
}

export function PlanningWeekProvider({ children }: PlanningWeekProviderProps) {
  const { weekStartsOn } = useSettings()
  const weekStartsOnNumber = weekStartsOn === 'monday' ? 1 : 0
  
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: weekStartsOnNumber })
  )

  const goToPreviousWeek = () => {
    const previousWeek = new Date(currentWeekStart)
    previousWeek.setDate(previousWeek.getDate() - 7)
    setCurrentWeekStart(startOfWeek(previousWeek, { weekStartsOn: weekStartsOnNumber }))
  }

  const goToNextWeek = () => {
    const nextWeek = new Date(currentWeekStart)
    nextWeek.setDate(nextWeek.getDate() + 7)
    setCurrentWeekStart(startOfWeek(nextWeek, { weekStartsOn: weekStartsOnNumber }))
  }

  const value: PlanningWeekContextType = {
    currentWeekStart,
    setCurrentWeekStart,
    goToPreviousWeek,
    goToNextWeek,
  }

  return (
    <PlanningWeekContext.Provider value={value}>
      {children}
    </PlanningWeekContext.Provider>
  )
}

export function usePlanningWeek() {
  const context = useContext(PlanningWeekContext)
  if (context === undefined) {
    throw new Error('usePlanningWeek must be used within a PlanningWeekProvider')
  }
  return context
}
